import * as React from 'react'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/react-hooks'

import {
  useLoginCheck,
  getSafeUrlParametersFromWindow,
} from '@thg-commerce/enterprise-core'
import { Tooltip } from '@thg-commerce/gravity-elements'
import { Appearance } from '@thg-commerce/gravity-elements/Tooltip/Tooltip'
import { pushToDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import { useWishlistNotificationContext } from '@thg-commerce/enterprise-core/src/WishlistNotificationProvider'
import { WishlistButtonStyleInterface } from '@thg-commerce/enterprise-pages/src/Product/theme'

import { WishlistTooltipContent } from './WishlistTooltipContent'
import {
  StyledButton,
  StyledSvgAddToWishlist,
  StyledHeartIcon,
  WishlistContentContainer,
} from './styles'

export interface WishlistButtonProps {
  sku: number
  inWishlist?: boolean
  heartButton?: boolean
  className?: string
  buttonWithoutText?: boolean
  i18nText: {
    addToWishlistText: string
    savedToWishlistText: string
    wishlistTooltip: {
      closeButtonText: string
    }
    wishlistTooltipContent: {
      loginSignupText: string
      toUseWishlistText: string
    }
  }
  addToWishlistCallback?: (sku: number) => void
  removeFromWishlistCallback?: (sku: number) => void
  buttonStyle?: WishlistButtonStyleInterface
}

export const ADD_PRODUCT_TO_WISHLIST_MUTATION = gql`
  mutation AddProductToWishlist($sku: SKU!) {
    addProductToWishlist(sku: $sku) @client
  }
`

export const REMOVE_PRODUCT_FROM_WISHLIST_MUTATION = gql`
  mutation RemoveProductFromWishlist($sku: SKU!) {
    removeProductFromWishlist(sku: $sku) @client
  }
`

export const WishlistButton = (props: WishlistButtonProps) => {
  const isLoggedIn = useLoginCheck()

  const [inWishlist, setInWishlist] = React.useState(props.inWishlist)
  const { showWishlistNotification } = useWishlistNotificationContext()

  React.useEffect(() => {
    setInWishlist(props.inWishlist)
  }, [props.inWishlist])

  const [addToWishlist] = useMutation<
    { addProductToWishlist: boolean },
    { sku: number }
  >(ADD_PRODUCT_TO_WISHLIST_MUTATION, {
    variables: {
      sku: props.sku,
    },
    onCompleted: (data) => {
      if (data?.addProductToWishlist) {
        showWishlistNotification?.current?.()
        setInWishlist(true)
        pushToDataLayer({
          type: 'elysiumEvent',
          eventData: {
            eventAction: 'added to wishlist',
            eventCategory: 'Wishlist Engagement',
            eventLabel: `${props.sku}`,
          },
        })
        props.addToWishlistCallback && props.addToWishlistCallback(props.sku)
      }
    },
  })

  const [removeFromWishlist] = useMutation<
    { removeProductFromWishlist: boolean },
    { sku: number }
  >(REMOVE_PRODUCT_FROM_WISHLIST_MUTATION, {
    variables: {
      sku: props.sku,
    },
    fetchPolicy: 'no-cache', // Void return type on mutation breaks cache-writing
    onCompleted: (data) => {
      data?.removeProductFromWishlist && setInWishlist(false)
      pushToDataLayer({
        type: 'elysiumEvent',
        eventData: {
          eventAction: 'removed from wishlist',
          eventCategory: 'Wishlist Engagement',
          eventLabel: `${props.sku}`,
        },
      })
      props.removeFromWishlistCallback &&
        props.removeFromWishlistCallback(props.sku)
    },
  })

  React.useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    const { addSKUToWishlist } = getSafeUrlParametersFromWindow<{
      addSKUToWishlist: string
    }>(window, ['addSKUToWishlist'])

    addSKUToWishlist &&
      parseInt(addSKUToWishlist, 10) === props.sku &&
      addToWishlist({ variables: { sku: props.sku } })
  }, [isLoggedIn])

  const onLoggedOutTooltipChange = (open: boolean) => {
    if (open && !isLoggedIn) {
      pushToDataLayer({
        type: 'elysiumEvent',
        eventData: {
          eventAction: 'clicked add to wishlist | logged out',
          eventCategory: 'Wishlist Engagement',
          eventLabel: `${props.sku}`,
        },
      })
    }
  }

  const onCloseLoggedOutTooltip = () => {
    pushToDataLayer({
      type: 'elysiumEvent',
      eventData: {
        eventAction: 'close login pop-up',
        eventCategory: 'Wishlist Engagement',
        eventLabel: `${props.sku}`,
      },
    })
  }

  if (!isLoggedIn) {
    return (
      <Tooltip
        className={props.className}
        appearance={Appearance.light}
        triggerContent={
          <StyledButton
            data-testid="add-to-wishlist-button"
            smallButton={props.buttonWithoutText}
            emphasis="low"
            sizing="regular"
            ariaLabel={props.i18nText.addToWishlistText}
            heartButton={props.heartButton}
            {...(props.buttonStyle ? { style: props.buttonStyle } : {})}
          >
            {props.heartButton ? (
              <StyledHeartIcon heartButton={props.heartButton} />
            ) : props.buttonWithoutText ? (
              <React.Fragment>
                <StyledSvgAddToWishlist />
              </React.Fragment>
            ) : (
              <WishlistContentContainer>
                <StyledSvgAddToWishlist style={{ fill: 'transparent' }} />
                {props.i18nText.addToWishlistText}
              </WishlistContentContainer>
            )}
          </StyledButton>
        }
        onChange={onLoggedOutTooltipChange}
        onClose={onCloseLoggedOutTooltip}
        content={
          <WishlistContentContainer>
            <WishlistTooltipContent
              sku={props.sku}
              i18nText={props.i18nText.wishlistTooltipContent}
            />
          </WishlistContentContainer>
        }
        i18nCloseButtonText={props.i18nText.wishlistTooltip.closeButtonText}
      />
    )
  }

  return (
    <StyledButton
      className={props.className}
      smallButton={props.buttonWithoutText}
      data-testid={
        inWishlist ? 'remove-from-wishlist-button' : 'add-to-wishlist-button'
      }
      emphasis="low"
      sizing="regular"
      onClick={() => {
        inWishlist
          ? removeFromWishlist({ variables: { sku: props.sku } })
          : addToWishlist({ variables: { sku: props.sku } })
      }}
      heartButton={props.heartButton}
      ariaLabel={
        inWishlist
          ? props.i18nText.savedToWishlistText
          : props.i18nText.addToWishlistText
      }
      {...(props.buttonStyle ? { style: props.buttonStyle } : {})}
    >
      {props.heartButton ? (
        inWishlist ? (
          <StyledHeartIcon
            heartButton={props.heartButton}
            style={{
              fill: props.buttonStyle?.active.icon.fill || 'currentColor',
              fillOpacity: props.buttonStyle?.active.icon.opacity || 1,
            }}
          />
        ) : (
          <StyledHeartIcon heartButton={props.heartButton} />
        )
      ) : props.buttonWithoutText && inWishlist ? (
        <React.Fragment>
          <StyledHeartIcon
            style={{
              fill: props.buttonStyle?.active.icon.fill || 'currentColor',
              fillOpacity: props.buttonStyle?.active.icon.opacity || 1,
            }}
          />
        </React.Fragment>
      ) : props.buttonWithoutText && !inWishlist ? (
        <React.Fragment>
          <StyledSvgAddToWishlist />
        </React.Fragment>
      ) : inWishlist ? (
        <WishlistContentContainer>
          <StyledHeartIcon />
          {props.i18nText.savedToWishlistText}
        </WishlistContentContainer>
      ) : (
        <WishlistContentContainer>
          <StyledSvgAddToWishlist style={{ fill: 'transparent' }} />
          {props.i18nText.addToWishlistText}
        </WishlistContentContainer>
      )}
    </StyledButton>
  )
}

export default WishlistButton
